// Navigation on the drivable-cell grid: A* over road cells, path smoothing, and pure-pursuit steering.
// Used by CPU bots, traffic, police and the headless AI player.

import { Ground, Surface } from './ground';
import type { CarState } from './car';

export interface Waypoint { x: number; z: number; }

const NEIGHBOURS = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]] as const;

export class Navigator {
  private drivable: Uint8Array;
  private cost: Float32Array;
  constructor(readonly ground: Ground) {
    const n = ground.cols * ground.rows;
    this.drivable = new Uint8Array(n);
    this.cost = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const t = ground.types[i];
      // roads are cheap, aprons ok, pavement allowed but costly, everything else blocked
      if (t === Surface.road) { this.drivable[i] = 1; this.cost[i] = 1; }
      else if (t === Surface.apron) { this.drivable[i] = 1; this.cost[i] = 1.3; }
      else if (t === Surface.pavement) { this.drivable[i] = 1; this.cost[i] = 4; }
      else if (t === Surface.grass || t === Surface.sand) { this.drivable[i] = 1; this.cost[i] = 7; }
      else { this.drivable[i] = 0; this.cost[i] = 1e9; }
    }
    // cells touching solid geometry (from the fine occupancy grid, inflated by a car radius) are blocked;
    // a band around them costs more so paths keep to the road centre
    for (let r = 0; r < ground.rows; r++) for (let c = 0; c < ground.cols; c++) {
      const i = r * ground.cols + c;
      if (!this.drivable[i]) continue;
      const x = this.cx(c), z = this.cz(r);
      if (ground.blockedNear(x, z, 1.4)) this.drivable[i] = 0;
      else if (ground.blockedNear(x, z, 3.0)) this.cost[i] += 3;
    }
    for (const o of ground.obstacles) { // trunks and poles
      const c = this.col(o.p[0]), r = this.row(o.p[2]);
      for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
        const cc = c + dc, rr = r + dr; if (!this.inside(cc, rr)) continue;
        const i = rr * ground.cols + cc; if (!this.drivable[i]) continue;
        const d = Math.hypot(this.cx(cc) - o.p[0], this.cz(rr) - o.p[2]);
        if (d < o.h[0] + 1.5) this.drivable[i] = 0; else if (d < o.h[0] + 3) this.cost[i] += 2;
      }
    }
  }

  /** Temporary extra cost around points where a driver got stuck; cleared by the caller. */
  private avoid: { x: number; z: number; r: number; cost: number }[] = [];
  setAvoid(points: { x: number; z: number; r: number; cost: number }[]): void { this.avoid = points; }

  col(x: number): number { return Math.floor((x - this.ground.origin[0]) / this.ground.cell); }
  row(z: number): number { return Math.floor((z - this.ground.origin[1]) / this.ground.cell); }
  cx(c: number): number { return this.ground.origin[0] + (c + 0.5) * this.ground.cell; }
  cz(r: number): number { return this.ground.origin[1] + (r + 0.5) * this.ground.cell; }
  private inside(c: number, r: number): boolean { return c >= 0 && r >= 0 && c < this.ground.cols && r < this.ground.rows; }

  /** Nearest drivable road cell to a point (spiral). */
  nearestCell(x: number, z: number, maxRings = 24): [number, number] | null {
    const c0 = this.col(x), r0 = this.row(z);
    for (let ring = 0; ring <= maxRings; ring++) {
      let best: [number, number, number] | null = null;
      for (let dr = -ring; dr <= ring; dr++) for (let dc = -ring; dc <= ring; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== ring) continue;
        const c = c0 + dc, r = r0 + dr;
        if (!this.inside(c, r)) continue;
        const i = r * this.ground.cols + c;
        if (!this.drivable[i] || this.cost[i] > 2) continue;
        const d = dc * dc + dr * dr;
        if (!best || d < best[2]) best = [c, r, d];
      }
      if (best) return [best[0], best[1]];
    }
    return null;
  }

  /** A* from a to b on the cell grid. Returns smoothed waypoints in world space, or null. */
  findPath(ax: number, az: number, bx: number, bz: number, maxExpand = 40000): Waypoint[] | null {
    const start = this.nearestCell(ax, az), goal = this.nearestCell(bx, bz);
    if (!start || !goal) return null;
    const W = this.ground.cols, H = this.ground.rows;
    const si = start[1] * W + start[0], gi = goal[1] * W + goal[0];
    const g = new Float32Array(W * H).fill(Infinity);
    const came = new Int32Array(W * H).fill(-1);
    const closed = new Uint8Array(W * H);
    const open = new MinHeap();
    const h = (i: number) => { const c = i % W, r = (i / W) | 0; return Math.hypot(c - goal[0], r - goal[1]); };
    g[si] = 0; open.push(si, h(si));
    let expanded = 0;
    while (open.size) {
      const cur = open.pop();
      if (cur === gi) break;
      if (closed[cur]) continue;
      closed[cur] = 1;
      if (++expanded > maxExpand) return null;
      const c = cur % W, r = (cur / W) | 0;
      for (const [dc, dr] of NEIGHBOURS) {
        const nc = c + dc, nr = r + dr;
        if (!this.inside(nc, nr)) continue;
        const ni = nr * W + nc;
        if (!this.drivable[ni] || closed[ni]) continue;
        // no corner cutting through blocked cells
        if (dc && dr && (!this.drivable[r * W + nc] || !this.drivable[nr * W + c])) continue;
        let extra = 0;
        if (this.avoid.length) { const x = this.cx(nc), z = this.cz(nr); for (const a of this.avoid) if (Math.hypot(x - a.x, z - a.z) < a.r) extra += a.cost; }
        const step = (dc && dr ? 1.4142 : 1) * (this.cost[ni] + extra);
        const ng = g[cur] + step;
        if (ng < g[ni]) { g[ni] = ng; came[ni] = cur; open.push(ni, ng + h(ni)); }
      }
    }
    if (came[gi] < 0 && gi !== si) return null;
    const cells: number[] = [];
    for (let i = gi; i >= 0; i = came[i]) { cells.push(i); if (i === si) break; }
    cells.reverse();
    const pts: Waypoint[] = cells.map((i) => ({ x: this.cx(i % W), z: this.cz((i / W) | 0) }));
    return this.smooth(pts);
  }

  /** Drop intermediate points when the straight line stays on cheap cells. */
  private smooth(pts: Waypoint[]): Waypoint[] {
    if (pts.length < 3) return pts;
    const out: Waypoint[] = [pts[0]];
    let i = 0;
    while (i < pts.length - 1) {
      let j = pts.length - 1;
      for (; j > i + 1; j--) if (this.clear(pts[i], pts[j])) break;
      out.push(pts[j]); i = j;
    }
    return out;
  }

  private clear(a: Waypoint, b: Waypoint): boolean {
    const d = Math.hypot(b.x - a.x, b.z - a.z); const n = Math.max(1, Math.ceil(d / (this.ground.cell * 0.5)));
    for (let k = 1; k < n; k++) {
      const t = k / n; const x = a.x + (b.x - a.x) * t, z = a.z + (b.z - a.z) * t;
      const c = this.col(x), r = this.row(z);
      if (!this.inside(c, r)) return false;
      const i = r * this.ground.cols + c;
      if (!this.drivable[i] || this.cost[i] > 2.5) return false;
    }
    return true;
  }

  isDrivable(x: number, z: number): boolean {
    const c = this.col(x), r = this.row(z);
    return this.inside(c, r) && !!this.drivable[r * this.ground.cols + c] && this.cost[r * this.ground.cols + c] < 3;
  }
}

/** Steering toward a path with pure pursuit. Returns steer in [-1,1] and a desired speed fraction. */
export function pursue(car: CarState, path: Waypoint[], idx: number, lookaheadBase = 6): { steer: number; speed: number; idx: number; offPath: number } {
  const lookahead = Math.max(4.5, Math.min(20, lookaheadBase + Math.abs(car.speed) * 0.32));
  // advance the index past points we have reached
  while (idx < path.length - 1 && Math.hypot(path[idx].x - car.x, path[idx].z - car.z) < lookahead * 0.55) idx++;
  const target = path[Math.min(idx, path.length - 1)];
  let ax = target.x, az = target.z;
  if (idx < path.length - 1) {
    const nxt = path[idx + 1]; const d = Math.hypot(target.x - car.x, target.z - car.z);
    if (d < lookahead) { const t = Math.min(1, (lookahead - d) / Math.max(1, Math.hypot(nxt.x - target.x, nxt.z - target.z))); ax = target.x + (nxt.x - target.x) * t; az = target.z + (nxt.z - target.z) * t; }
  }
  const dx = ax - car.x, dz = az - car.z;
  const desired = Math.atan2(dx, dz);
  let err = desired - car.yaw;
  while (err > Math.PI) err -= 2 * Math.PI;
  while (err < -Math.PI) err += 2 * Math.PI;
  const steer = Math.max(-1, Math.min(1, err * 1.8));
  // how far are we from the segment we should be on?
  const prev = path[Math.max(0, idx - 1)];
  const offPath = distToSegment(car.x, car.z, prev.x, prev.z, target.x, target.z);
  // slow for sharp turns, upcoming corners, and at the end of the path
  const turn = Math.abs(err);
  let speed = turn > 0.9 ? 0.3 : turn > 0.45 ? 0.55 : turn > 0.2 ? 0.8 : 1;
  if (idx < path.length - 1) {
    const nxt = path[idx + 1]; const a1 = Math.atan2(target.x - car.x, target.z - car.z), a2 = Math.atan2(nxt.x - target.x, nxt.z - target.z);
    let bend = Math.abs(a2 - a1); while (bend > Math.PI) bend = Math.abs(bend - 2 * Math.PI);
    const dTurn = Math.hypot(target.x - car.x, target.z - car.z);
    if (bend > 0.7 && dTurn < 18) speed = Math.min(speed, 0.5);
  }
  const remaining = path.length - 1 - idx;
  if (remaining === 0 && Math.hypot(dx, dz) < 14) speed = Math.min(speed, 0.4);
  return { steer, speed, idx, offPath };
}

function distToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const vx = bx - ax, vz = bz - az; const l2 = vx * vx + vz * vz;
  let t = l2 ? ((px - ax) * vx + (pz - az) * vz) / l2 : 0; t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + vx * t), pz - (az + vz * t));
}

class MinHeap {
  private keys: number[] = []; private vals: number[] = [];
  get size(): number { return this.keys.length; }
  push(v: number, k: number): void {
    this.keys.push(k); this.vals.push(v);
    let i = this.keys.length - 1;
    while (i > 0) { const p = (i - 1) >> 1; if (this.keys[p] <= this.keys[i]) break; this.swap(i, p); i = p; }
  }
  pop(): number {
    const top = this.vals[0]; const lk = this.keys.pop()!, lv = this.vals.pop()!;
    if (this.keys.length) {
      this.keys[0] = lk; this.vals[0] = lv; let i = 0;
      for (;;) { const l = 2 * i + 1, r = l + 1; let m = i; if (l < this.keys.length && this.keys[l] < this.keys[m]) m = l; if (r < this.keys.length && this.keys[r] < this.keys[m]) m = r; if (m === i) break; this.swap(i, m); i = m; }
    }
    return top;
  }
  private swap(a: number, b: number): void { [this.keys[a], this.keys[b]] = [this.keys[b], this.keys[a]]; [this.vals[a], this.vals[b]] = [this.vals[b], this.vals[a]]; }
}
